import React from 'react';
import {
  Form, ICON_MAGNIFY, AutoCompleteV2OnSuggestionSelected,
  AutoCompleteV2,
  FormAutoCompleteV2Props,
  FormField,
} from '@doveit/bricks';
import { AgentStatus } from '../../domain/types';
import { searchAgents } from '../../providers/api/agent/agentProvider';
import { Agent } from '../../providers/api/dtos';
import { JobTitle } from '../../domain/types/jobTitle';
import { SortOrder } from '../../providers/pagination';
import { getAgentName } from '../../agent/utils/utils';

export type AgentAutoCompleteProps = (Omit<FormAutoCompleteV2Props<Agent>, 'suggestions' | 'printSuggestion' | 'name'>) & {
  name?: string,
  status?: AgentStatus,
  required?: boolean,
  startFrom?: string | number,
  self?: Agent,
  maxSuggestions?: number,
  jobTitles?: JobTitle[]
  excludeAgentsByEmail?: string[],
  excludeAgentsById?: number[],
  agencyId?: number | number[],
  coordinatedBy?: string,
  onSuggestionSelected?: AutoCompleteV2OnSuggestionSelected<Agent>,
  printSuggestion?: (agent: Agent | Agent[]) => string
  printLabelSuggestion?: (agent: Agent | Agent[]) => string
};

const defaultPrintSuggestion = (agent: Agent | Agent[]) => {
  if (Array.isArray(agent)) return '';

  return getAgentName(agent)!;
};

const AgentAutoComplete: React.FC<AgentAutoCompleteProps> = ({
  status,
  startFrom,
  maxSuggestions,
  jobTitles = [],
  excludeAgentsByEmail = [],
  excludeAgentsById = [],
  agencyId,
  coordinatedBy,
  onSuggestionSelected,
  name,
  label,
  required,
  error,
  warning,
  printSuggestion = defaultPrintSuggestion,
  printLabelSuggestion,
  ...rest
}) => {
  const fetchSuggestions = React.useCallback(async (search: string) => {
    const q = search.trim();

    const { content } = await searchAgents({
      q,
      active: status,
      startFrom,
      jobTitle: jobTitles,
      excludeEmails: excludeAgentsByEmail,
      excludeIds: excludeAgentsById,
      agencyId,
      coordinatedBy,
    }, {
      size: maxSuggestions,
      sort: {
        name: SortOrder.ASC,
        surname: SortOrder.ASC,
      },
    });

    return content;
  }, [status, startFrom, jobTitles, excludeAgentsByEmail, excludeAgentsById, agencyId, coordinatedBy, maxSuggestions]);

  const commonProps = React.useMemo(() => ({
    'aria-label': 'Campo per inserimento agente',
    icon: ICON_MAGNIFY,
    suggestions: fetchSuggestions,
    printSuggestion,
    printLabelSuggestion,
    onSuggestionSelected,
  }), [fetchSuggestions, onSuggestionSelected, printSuggestion, printLabelSuggestion]);

  return name ? (
    <Form.AutoCompleteV2
      {...rest}
      {...commonProps}
      warning={warning}
      required={required}
      name={name}
      label={label}
    />
  ) : (
    <FormField
      label={label}
      required={required}
      error={error}
    >
      <AutoCompleteV2
        {...rest}
        {...commonProps}
        error={!!error}
      />
    </FormField>
  );
};

export default AgentAutoComplete;
